export const ERROR_TYPE = {
  FORM_ERROR: 1,
  FATAL_ERROR: 2,
  TFA_REQUIRED: 3,
  TFA_SETUP_REQUIRED: 4,
  INSTALLATION_PROVISIONING: 5,
  INSTALLATION_VALIDATION_PENDING: 6,
};

export function handleAPIError(store, err, defaultMessage) {
  if (!err) return null;

  if (!err.response || !err.response.data) {
    // eslint-disable-next-line
    console.error('unknown error', err);
    return { type: ERROR_TYPE.FORM_ERROR, err: defaultMessage };
  }

  if (err.response.status === 502) {
    store.commit('showFatalError', {
      error: 'Internal Server Error',
      message: 'An error has occurred, please try again later.',
      appError: 'server_error',
      appErrorExplanation: '502 bad gateway',
    });
    return { type: ERROR_TYPE.FATAL_ERROR, err: '502 bad gateway' };
  }

  const data = err.response.data;

  if (err.response.status === 429 && data.status === 'Too many attempts') {
    return { type: ERROR_TYPE.FORM_ERROR, err: data.error };
  }

  if (err.response.status === 401 && data.status === 'two factor auth setup required') {
    return { type: ERROR_TYPE.TFA_SETUP_REQUIRED, err: data.error };
  }

  if (err.response.status === 401 && data.status === 'Wrong credentials') {
    return { type: ERROR_TYPE.FORM_ERROR, err: data.error };
  }

  if (err.response.status === 401 && data.status === 'email verification required') {
    return { type: ERROR_TYPE.INSTALLATION_VALIDATION_PENDING, err: data.error };
  }

  if (data.status && data.status.indexOf('twoFactorPasscode') > -1) {
    if (data.status.indexOf('required') > -1) {
      return { type: ERROR_TYPE.TFA_REQUIRED, err: data.status };
    }
    if (data.status.indexOf('incorrect') > -1) {
      return { type: ERROR_TYPE.FORM_ERROR, err: 'Two Factor Authentication code is incorrect.' };
    }
  }

  // Try and figure out the error from all the random formats we have :/
  let error = '';
  if (data.error) error = data.error;
  if (data.errors && data.errors.length) error = data.errors[0].message || data.errors[0];
  if (!error && data.message) error = data.message;

  // Errors provided by validate lib.
  if (data.errors && data.errors.constructor === Object) {
    error = Object.keys(data.errors).map((key) => `${key} ${data.errors[key]}`).join(',');
    store.commit('showFatalError', {
      error: 'Validation failed',
      message: error,
      appError: 'validation_failed',
      appErrorExplanation: error,
    });
    return { type: ERROR_TYPE.FATAL_ERROR, err: error };
  }

  // Hopefully we have something to handle now
  switch (error) { // eslint-disable-line
    case 'Site not found':
      store.commit('setSiteNotFound');
      return { type: ERROR_TYPE.FATAL_ERROR, err: error };
    case 'Internal server error':
      store.commit('showFatalError', {
        error: 'Internal Server Error',
        message: 'An error has occurred, please try again later.',
        appError: 'server_error',
        appErrorExplanation: 'an internal server error has occurred',
      });
      return { type: ERROR_TYPE.FATAL_ERROR, err: error };
    case 'new user provisioning is disabled':
      store.commit('showFatalError', {
        error: 'Failed to Login',
        explanation: data.explanation,
        appError: 'access_denied',
        appErrorExplanation: 'new saml user was denied access',
      });
      return { type: ERROR_TYPE.FATAL_ERROR, err: error };
    case 'not enough seats':
      store.commit('showFatalError', {
        error: 'Failed to Login',
        explanation: data.explanation,
        appError: 'access_denied',
        appErrorExplanation: 'not enough seats for new saml user',
      });
      return { type: ERROR_TYPE.FATAL_ERROR, err: error };
    case 'Authentication timed out':
      store.commit('showFatalError', {
        error: 'Failed to Login',
        explanation: 'Authentication timed out, please log in again.',
        appError: 'access_denied',
        appErrorExplanation: 'saml login token has expired',
      });
      return { type: ERROR_TYPE.FATAL_ERROR, err: error };
    case 'your IP address is not in the whitelist':
      store.commit('showFatalError', {
        error: 'Access Denied',
        message: 'Access from your IP address is not enabled.',
        explanation: 'Please contact your administrator to give access to your current IP address or log in from an allowed IP.',
        appError: 'access_denied',
        appErrorExplanation: 'user ip is not whitelisted',
      });
      return { type: ERROR_TYPE.FATAL_ERROR, err: error };
    case 'Invite Expired':
      return { type: ERROR_TYPE.FORM_ERROR, err: 'Invite Expired' };
    case 'User not invited':
      return { type: ERROR_TYPE.FORM_ERROR, err: 'User not invited' };
    case 'User not found':
      return { type: ERROR_TYPE.FORM_ERROR, err: 'User not found' };
    case 'Invalid password. Cannot be blank':
      return { type: ERROR_TYPE.FORM_ERROR, err: 'Password cannot be blank.' };
    case 'your password must contain at least 8 characters':
      return { type: ERROR_TYPE.FORM_ERROR, err: 'Password must be at least 8 characters long.' };
    case 'your password must include at least 2 numeric characters':
      return { type: ERROR_TYPE.FORM_ERROR, err: 'Password must contain at least 2 numeric characters.' };
    case 'your password must include at least one letter':
      return { type: ERROR_TYPE.FORM_ERROR, err: 'Password must contain at least one letter.' };
    case 'User handle is not available':
      return { type: ERROR_TYPE.FORM_ERROR, err: 'The handle you have chosen is already in use.' };
    case 'Standard login is not available on your account':
      return { type: ERROR_TYPE.FORM_ERROR, err: 'Standard login is not available on your account.' };
    case 'Installation is being setup':
      return { type: ERROR_TYPE.INSTALLATION_PROVISIONING, err: 'Installation is provisioning' };
    // app login flow errors:
    case 'app has not yet been verified':
      store.commit('showFatalError', {
        error: 'App not yet verified',
        message: 'This app has not yet been verified.',
        explanation: 'Please check your app status in the Developer Portal.',
        appError: 'access_denied',
        appErrorExplanation: 'app has not yet been verified',
      });
      return { type: ERROR_TYPE.FATAL_ERROR, err: error };
    case 'provided redirect_uri is not on the allowed list':
      store.commit('showFatalError', {
        error: 'Invalid redirect_uri',
        message: 'The redirect_uri provided is not on the list of allowed URIs.',
        explanation: 'Please check your parameters and your app settings in the Developer Portal.',
        appError: 'access_denied',
        appErrorExplanation: 'provided redirect_uri is not on the allowed list',
      });
      return { type: ERROR_TYPE.FATAL_ERROR, err: error };
    case 'requested scope does not match app scope':
      store.commit('showFatalError', {
        error: 'Invalid scope',
        message: 'The requested scope is invalid.',
        explanation: 'Please check your parameters and your app settings in the Developer Portal.',
        appError: 'invalid_scope',
        appErrorExplanation: 'requested scope does not match app scope',
      });
      return { type: ERROR_TYPE.FATAL_ERROR, err: error };
    case 'client_id is invalid':
      store.commit('showFatalError', {
        error: 'App not found',
        message: 'The client_id was either not provided or does not match any app.',
        explanation: 'Please update your app or contact the app developer if the issue persists. If you are the developer, please check your parameters and your app settings in the Developer Portal.',
        appError: 'access_denied',
        appErrorExplanation: 'invalid client_id',
      });
      return { type: ERROR_TYPE.FATAL_ERROR, err: error };
    case 'client_secret is invalid':
      store.commit('showFatalError', {
        error: 'Invalid client secret',
        message: 'The client_secret provided is invalid.',
        explanation: 'Please check your parameters and your app settings in the Developer Portal.',
        appError: 'access_denied',
        appErrorExplanation: 'invalid client_secret',
      });
      return { type: ERROR_TYPE.FATAL_ERROR, err: error };
    case 'origin is invalid':
      store.commit('showFatalError', {
        error: 'Invalid origin',
        message: 'The origin provided is invalid.',
        explanation: 'Please check your parameters and your app settings in the Developer Portal.',
        appError: 'access_denied',
        appErrorExplanation: 'invalid origin',
      });
      return { type: ERROR_TYPE.FATAL_ERROR, err: error };
    case 'The token provided is invalid or has expired':
      store.commit('showFatalError', {
        error: 'Invalid token',
        message: 'The token provided is invalid or has expired.',
        explanation: 'Please try again.',
        appError: 'access_denied',
        appErrorExplanation: 'invalid token',
      });
      return { type: ERROR_TYPE.FATAL_ERROR, err: error };
    case 'requested scope requires admin access':
      store.commit('showFatalError', {
        error: 'Access denied',
        message: 'This action requires admin permissions.',
        explanation: 'Please contact a site administrator.',
        appError: 'access_denied',
        appErrorExplanation: 'user requires admin permissions',
      });
      return { type: ERROR_TYPE.FATAL_ERROR, err: error };
    case 'app cannot be authorized':
      store.commit('showFatalError', {
        error: 'App cannot be authorized',
        message: 'The app must be installed.',
        explanation: 'Please contact a site administrator.',
        appError: 'app_cannot_be_authorized',
        appErrorExplanation: 'app must be installed',
      });
      return { type: ERROR_TYPE.FATAL_ERROR, err: error };
    case 'app does not support oauth':
      store.commit('showFatalError', {
        error: 'App does not support OAUTH',
        message: 'This app does not support authorization',
        appError: 'app_does_not_support_oauth',
        appErrorExplanation: 'app does not support oauth',
      });
      return { type: ERROR_TYPE.FATAL_ERROR, err: error };
  }

  // SAML auth error
  if (error.indexOf('illegal base64 data at input byte') > -1) {
    store.commit('showFatalError', {
      error: 'Login Failed',
      message: 'Single Sign On failed, we received invalid parameters.',
      appError: 'server_error',
      appErrorExplanation: 'saml authentication failed, invalid iv and samlauth params',
    });
    return { type: ERROR_TYPE.FATAL_ERROR, err: error };
  }

  return { type: ERROR_TYPE.FORM_ERROR, err: defaultMessage };
}



// WEBPACK FOOTER //
// ./src/utils/errorHelper.js